import type { PaategningFilingKind, ServitutFilingTextGroup } from "src/dataCollector/parseDokumentRevisionssporXml";

export type FilingPdfHint = {
   kind: PaategningFilingKind;
   tidspunkt?: string;
   anmelderName?: string;
   textSnippet?: string;
};

function firstMeaningfulLine(text: string): string {
   return text.split(/\n/).map((line) => line.trim()).find(Boolean) || "";
}

function compactText(text: string): string {
   return text.replace(/\s+/g, " ").trim();
}

function pageContainsSnippet(pageText: string, snippet: string): boolean {
   const needle = compactText(snippet);
   if (!needle) {
      return false;
   }
   return compactText(pageText).includes(needle);
}

function startsOtherFiling(pageText: string, kind: PaategningFilingKind, snippet: string): boolean {
   if (pageContainsSnippet(pageText, snippet)) {
      return false;
   }
   const first = firstMeaningfulLine(pageText);
   const head = pageText.slice(0, 360);
   if (kind !== "paategning" && (/^\s*Påtegning\b/.test(first) || /^\s*Påtegning\b/m.test(head))) {
      return true;
   }
   if (kind !== "servitut" && (
      first === "Servitut"
      || /Dokumenttype:\s*Servitut\b/.test(head)
      || /^\s*Deklaration\b/.test(first)
      || (/\bServitut\b/.test(head) && !/\bPåtegning\b/.test(head))
   )) {
      return true;
   }
   if (kind !== "matrikulaerAendring" && /Matrikulær ændring/.test(head)) {
      return true;
   }
   return false;
}

export function filingDateDots(tidspunkt?: string): string {
   const date = tidspunkt?.split("T")[0] || "";
   const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
   if (!match) {
      return "";
   }
   return `${match[3]}.${match[2]}.${match[1]}`;
}

export function filingTextSnippet(groups: ServitutFilingTextGroup[]): string {
   const group = groups[0];
   if (!group) {
      return "";
   }
   return (group.heading || group.paragraphs[0] || "").trim();
}

export function filingHintFromFiling(filing: {
   kind: PaategningFilingKind;
   tidspunkt?: string;
   anmelder?: { name?: string } | null;
   textGroups?: ServitutFilingTextGroup[];
}): FilingPdfHint {
   return {
      kind: filing.kind,
      tidspunkt: filing.tidspunkt,
      anmelderName: filing.anmelder?.name,
      textSnippet: filingTextSnippet(filing.textGroups ?? []),
   };
}

export function pageIndexesForFiling(pageTexts: string[], hint: FilingPdfHint): number[] {
   const snippet = hint.textSnippet?.trim() || "";
   if (snippet) {
      const hits = pageTexts
         .map((text, index) => (pageContainsSnippet(text, snippet) ? index : -1))
         .filter((index) => index >= 0);
      if (hits.length) {
         let start = hits[0];
         let end = hits[hits.length - 1];
         while (end + 1 < pageTexts.length && !startsOtherFiling(pageTexts[end + 1], hint.kind, snippet)) {
            end += 1;
         }
         while (start > 0 && !startsOtherFiling(pageTexts[start - 1], hint.kind, snippet)) {
            const previous = pageTexts[start - 1];
            const first = firstMeaningfulLine(previous);
            if (!/^(Dato\/løbenummer|Servitut|Påtegning)\b/.test(first)) {
               break;
            }
            start -= 1;
         }
         return Array.from({ length: end - start + 1 }, (_, offset) => start + offset);
      }
   }

   const date = filingDateDots(hint.tidspunkt);
   const scores = pageTexts.map((text) => {
      let score = 0;
      if (hint.anmelderName && text.includes(hint.anmelderName)) {
         score += 4;
      }
      if (date && text.includes(date)) {
         score += 3;
      }
      if (hint.kind === "matrikulaerAendring" && /Matrikulær ændring/.test(text)) {
         score += 1;
      }
      return score;
   });
   const best = Math.max(...scores, 0);
   if (best < 3) {
      return [];
   }
   const first = scores.findIndex((score) => score === best);
   let end = first;
   while (end + 1 < pageTexts.length && scores[end + 1] > 0 && !startsOtherFiling(pageTexts[end + 1], hint.kind, snippet)) {
      end += 1;
   }
   let start = first;
   while (start > 0 && scores[start - 1] > 0 && !startsOtherFiling(pageTexts[start - 1], hint.kind, snippet)) {
      start -= 1;
   }
   return Array.from({ length: end - start + 1 }, (_, offset) => start + offset);
}

export async function extractPdfPageTexts(source: Blob | ArrayBuffer): Promise<string[]> {
   const [{ pdfjs }, worker] = await Promise.all([
      import("react-pdf"),
      import("pdfjs-dist/build/pdf.worker.min.mjs?url"),
   ]);
   pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
   const data = source instanceof Blob ? await source.arrayBuffer() : source;
   const pdf = await pdfjs.getDocument({ data }).promise;
   const pages: string[] = [];
   for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
      const page = await pdf.getPage(pageNumber);
      const content = await page.getTextContent();
      pages.push(
         content.items.map((item) => ("str" in item ? String(item.str || "") : "")).join(" "),
      );
   }
   return pages;
}

export async function slicePdfPages(source: Blob, pageIndexes: number[]): Promise<Blob> {
   if (!pageIndexes.length) {
      return source;
   }
   const { PDFDocument } = await import("pdf-lib");
   const loaded = await PDFDocument.load(await source.arrayBuffer());
   const last = loaded.getPageCount() - 1;
   const valid = [...new Set(pageIndexes.filter((index) => index >= 0 && index <= last))];
   if (!valid.length) {
      return source;
   }
   const out = await PDFDocument.create();
   const copied = await out.copyPages(loaded, valid);
   copied.forEach((page) => out.addPage(page));
   const pdfBytes = await out.save();
   return new Blob([new Uint8Array(pdfBytes)], { type: "application/pdf" });
}
